// Lot 600 — WebSocket d'une salle de direct (hôte + spectateurs) avec reconnexion automatique.
// Une coupure réseau fermait la socket pour de bon : plus de chat, de réactions ni de compteur
// jusqu'à la fin du direct. Ici : repli exponentiel 1 s → 8 s, reprise immédiate au retour du réseau
// (`online`), arrêt définitif sur les codes applicatifs 44xx (auth refusée, direct terminé…).
const FATAL_MIN = 4400;
const FATAL_MAX = 4499;
const MAX_DELAY_MS = 8000;

export function openLiveSocket(urlFn, { onMessage, onOpen, onReconnect } = {}) {
  let ws = null;
  let closed = false;
  let attempt = 0;
  let timer = null;
  let everOpened = false;

  const schedule = () => {
    clearTimeout(timer);
    const delay = Math.min(1000 * 2 ** attempt, MAX_DELAY_MS);
    attempt += 1;
    timer = setTimeout(connect, delay);
  };

  const connect = () => {
    if (closed) return;
    let sock;
    try { sock = new WebSocket(urlFn()); } catch { schedule(); return; }
    ws = sock;
    sock.onopen = () => {
      if (sock !== ws) return;
      const resumed = everOpened;
      everOpened = true;
      attempt = 0;
      onOpen?.();
      if (resumed) onReconnect?.();
    };
    sock.onmessage = (ev) => {
      if (sock !== ws) return;
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      onMessage?.(msg);
    };
    sock.onclose = (ev) => {
      if (sock !== ws) return;
      ws = null;
      if (closed) return;
      const code = ev?.code || 0;
      if (code >= FATAL_MIN && code <= FATAL_MAX) { closed = true; return; }
      schedule();
    };
    sock.onerror = () => { /* onclose suit toujours */ };
  };

  const onOnline = () => {
    if (closed || (ws && ws.readyState === 1)) return;
    clearTimeout(timer);
    attempt = 0;
    try { ws?.close(); } catch { /* noop */ }
    ws = null;
    connect();
  };
  if (typeof window !== "undefined") window.addEventListener("online", onOnline);
  connect();

  return {
    send(msg) {
      if (!ws || ws.readyState !== 1) return false;
      try { ws.send(typeof msg === "string" ? msg : JSON.stringify(msg)); return true; } catch { return false; }
    },
    get readyState() { return ws ? ws.readyState : 3; },
    get closed() { return closed; },
    close() {
      closed = true;
      clearTimeout(timer);
      if (typeof window !== "undefined") window.removeEventListener("online", onOnline);
      const s = ws; ws = null;
      try { s?.close(); } catch { /* noop */ }
    },
  };
}
